import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import BrandLoader from '../components/Brand/BrandLoader';
import { getUser } from '../utils/func';
import { appLocalStorage } from '../utils/storage';
import config from '../utils/config';
import appLinks from '../appLinks/config.json';
import { readPreferredLanguage, validLanguage } from '../utils/languagePreference';
import { deletionCopy } from './copy';
import { applyDeletionProof, deletionRequest, receiveReceipt, receiptFragment, submitDeletion } from './receipt';
import './accountDeletion.scss';

export default function AccountDeletionPage() {
  const { i18n } = useTranslation('brand', { useSuspense: false });
  const pathLanguage = window.location.pathname.split('/')[1];
  const storedLanguage = readPreferredLanguage() || String(i18n.language || 'en').split('-')[0];
  const language = validLanguage(pathLanguage) ? pathLanguage : validLanguage(storedLanguage) ? storedLanguage : 'en';
  const copy = deletionCopy(language);
  // This page deliberately bypasses session/translation initialization. Keep
  // its links and saved receipt in the locale actually shown, not stale i18n.
  const localizedPath = path => `${language === 'en' ? '' : `/${language}`}${path}`;
  const location = useLocation();
  const statusOnly = location.state?.statusOnly === true;
  const [receipt] = useState(() => statusOnly ? null : receiveReceipt());
  const [status, setStatus] = useState(() => statusOnly ? location.state?.verifiedDeletion : null);
  const [loading, setLoading] = useState(Boolean(receipt));
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!receipt) return;
    let active = true;
    const controller = new AbortController();
    let timer;
    const check = async () => {
      try {
        const value = await deletionRequest('/account-deletions/status', {
          request_id: receipt.request_id, receipt_secret: receipt.receipt_secret,
        }, { signal: controller.signal });
        await applyDeletionProof(receipt, value);
        if (!active) return;
        setStatus(value); setError(false);
        if (value.state !== 'completed') timer = setTimeout(check, 15000);
      } catch { if (active) setError(true); }
      finally { if (active) setLoading(false); }
    };
    setLoading(true); check();
    return () => { active = false; clearTimeout(timer); controller.abort(); };
  }, [receipt, retry]);

  const save = async () => {
    // A receipt contains no email, profile or login token. The fragment grants
    // status access only and is never sent to the website in an HTTP request.
    const origin = Capacitor.isNativePlatform() ? appLinks.origin : window.location.origin;
    const content = `${origin.replace(/\/$/, '')}${localizedPath('/account-deletion')}#${receiptFragment(receipt)}\n`;
    // Do not use shareLink: its marketing telemetry must never receive a receipt.
    if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('Share')) {
      try { await Share.share({ title: copy.title, text: content }); } catch { /* dismissal keeps the local receipt */ }
      return;
    }
    const objectUrl = URL.createObjectURL(new Blob([content], { type: 'text/plain;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = objectUrl; link.download = `wiw-deletion-${receipt.request_id}.txt`;
    link.click(); setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  };
  const resend = async () => {
    if (!receipt || loading || receipt.owner_user_id !== getUser()?.id || receipt.accepted) return;
    setLoading(true);
    try { const value = await submitDeletion(receipt, appLocalStorage.getItem(config.token)); setStatus(value); setError(false); }
    catch { setError(true); }
    finally { setLoading(false); setRetry(value => value + 1); }
  };
  const message = !receipt && !status ? (statusOnly ? copy.unavailable : copy.missing) : error ? copy.unavailable : status?.state === 'completed' ? copy.completed
    : status?.access_closed ? copy.closed : copy.registered;
  return <main className="account-deletion-page">
    <Helmet><html lang={language} /><title>{copy.title}</title><meta name="robots" content="noindex,nofollow" /><meta name="referrer" content="no-referrer" /></Helmet>
    <h1>{copy.title}</h1>
    <div role="status" aria-live="polite">{loading && !status ? <BrandLoader /> : <p>{message}</p>}</div>
    {status?.provider_outcome === 'legacy_unavailable' && <p>{copy.appleManual}</p>}
    {status?.provider_outcome === 'pending' && <p>{copy.applePending}</p>}
    {status?.backup_expiry && <p>{copy.backupUntil} {new Intl.DateTimeFormat(language, { dateStyle: 'medium' }).format(new Date(status.backup_expiry))}.</p>}
    {receipt && <div className="account-deletion-actions">
      <button className="button_border" disabled={loading} onClick={() => setRetry(value => value + 1)}>{copy.check}</button>
      <button className="button_border" onClick={save}>{copy.save}</button>
      {error && !receipt.accepted && receipt.owner_user_id === getUser()?.id
        && <button className="button_border" disabled={loading} onClick={resend}>{copy.resend}</button>}
    </div>}
    {!receipt && <Link className="button_border" to={localizedPath('/login') + '?deletion_status=1'}>{copy.check}</Link>}
    <Link className="button_border" to={localizedPath('/')}>{copy.browse}</Link>
    {status?.state === 'completed' && <Link className="button_border" to={localizedPath('/login') + '?register_new=1'}>{copy.newAccount}</Link>}
  </main>;
}
